'use client'

import * as React from 'react'
import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from '../helpers/index.js'

/**
 * Progress Bar Variants
 */
const progressVariants = cva('rounded-box relative w-full ', {
  variants: {
    variant: {
      default: 'bg-container-secondary-0 overflow-hidden',
      withHandle: 'bg-container-secondary-0',
    },
    size: {
      base: 'h-2',
      lg: 'h-3',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'base',
  },
})

const progressBarVariants = cva('rounded-box h-full ', {
  variants: {
    variant: {
      default: 'bg-info-primary ',
      withHandle: 'bg-info-primary ',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const handleVariants = cva('border-info-white bg-info-primary size-4 rounded-full border shadow-sm', {
  variants: {
    size: {
      base: 'size-4',
      lg: 'tablet:size-5 size-4',
    },
  },
  defaultVariants: {
    size: 'base',
  },
})

export interface ProgressClassNames {
  /** 根容器的样式类名 */
  root?: string
  /** 进度条的样式类名 */
  progressBar?: string
  /** 拖拽手柄的样式类名 */
  handle?: string
}

export interface ProgressProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'>, VariantProps<typeof progressVariants> {
  /** 当前进度值 */
  value: number
  /** 最大值，默认为 100 */
  max?: number
  /** 最小值，默认为 0 */
  min?: number
  /** 进度条的 aria-label，用于屏幕阅读器 */
  'aria-label'?: string
  /** 进度条的 aria-labelledby，关联到标签元素的ID */
  'aria-labelledby'?: string
  /** 进度条描述，用于屏幕阅读器 */
  'aria-describedby'?: string
  /** 值改变时的回调函数（仅在withHandle变体时有效） */
  onValueChange?: (value: number) => void
  /** 是否禁用交互（仅在withHandle变体时有效） */
  disabled?: boolean
  /** 拖拽步长，默认为1 */
  step?: number
  /** 自定义样式类名，用于覆盖不同部分的默认样式 */
  classNames?: ProgressClassNames
}

/**
 * Progress - 进度条
 *
 * @description 用于显示任务完成进度的可视化组件，支持多种尺寸、颜色变体和无障碍访问
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      value,
      max = 100,
      min = 0,
      variant,
      size,
      className,
      classNames,
      onValueChange,
      disabled = false,
      step = 1,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const progressRef = React.useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = React.useState(false)
    const dragRectRef = React.useRef<DOMRect | null>(null)

    // 确保 value 在有效范围内
    const normalizedValue = Math.max(min, Math.min(max, value))
    // 计算百分比
    const percentage = ((normalizedValue - min) / (max - min)) * 100
    // 是否显示handle
    const isWithHandle = variant === 'withHandle'
    // 是否可交互
    const isInteractive = isWithHandle && !disabled && onValueChange

    // 拖拽时使用缓存的 rect 避免重复计算
    const handleDragMove = React.useCallback(
      (clientX: number) => {
        if (!dragRectRef.current || !onValueChange) return

        const rect = dragRectRef.current
        const offsetX = clientX - rect.left
        const percentage = Math.max(0, Math.min(1, offsetX / rect.width))
        const rawValue = min + percentage * (max - min)
        const clampedValue = Math.max(min, Math.min(max, rawValue))

        onValueChange(clampedValue)
      },
      [min, max, onValueChange]
    )

    // 拖拽结束时应用步长
    const handleDragEnd = React.useCallback(
      (clientX: number) => {
        if (!dragRectRef.current || !onValueChange) return

        const rect = dragRectRef.current
        const offsetX = clientX - rect.left
        const percentage = Math.max(0, Math.min(1, offsetX / rect.width))
        const rawValue = min + percentage * (max - min)
        const steppedValue = Math.round(rawValue / step) * step
        const clampedValue = Math.max(min, Math.min(max, steppedValue))

        onValueChange(clampedValue)
        dragRectRef.current = null // 清理缓存
      },
      [min, max, step, onValueChange]
    )

    // 鼠标按下事件
    const handleMouseDown = React.useCallback(
      (event: React.MouseEvent) => {
        if (!isInteractive || !progressRef.current) return

        event.preventDefault()
        // 只在开始拖拽时获取一次 rect，避免拖拽过程中重复计算
        dragRectRef.current = progressRef.current.getBoundingClientRect()
        setIsDragging(true)
        handleDragMove(event.clientX)
      },
      [isInteractive, handleDragMove]
    )

    // 全局鼠标移动事件
    React.useEffect(() => {
      if (!isDragging || !isInteractive) return

      const handleMouseMove = (event: MouseEvent) => {
        handleDragMove(event.clientX)
      }

      const handleMouseUp = (event: MouseEvent) => {
        handleDragEnd(event.clientX)
        setIsDragging(false)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }, [isDragging, isInteractive, handleDragMove, handleDragEnd])

    // 合并refs
    const combinedRef = React.useCallback(
      (node: HTMLDivElement) => {
        if (progressRef) {
          // @ts-ignore
          progressRef.current = node
        }
        if (ref) {
          if (typeof ref === 'function') {
            ref(node)
          } else {
            ref.current = node
          }
        }
      },
      [ref]
    )

    return (
      <div
        ref={combinedRef}
        role={isInteractive ? 'slider' : 'progressbar'}
        aria-valuenow={normalizedValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled}
        className={cn(
          progressVariants({ variant, size }),
          disabled && 'cursor-not-allowed opacity-50',
          isInteractive && !disabled && 'cursor-pointer',
          className,
          classNames?.root
        )}
        onMouseDown={isInteractive ? handleMouseDown : undefined}
        {...props}
      >
        <div
          className={cn(progressBarVariants({ variant }), classNames?.progressBar)}
          style={{
            width: `${Math.max(0, Math.min(100, percentage))}%`,
          }}
          data-testid="progress-bar"
        />
        {isWithHandle && percentage > 0 && (
          <div
            className={cn(
              handleVariants({ size }),
              disabled && 'opacity-50',
              isDragging && 'scale-110 shadow-lg',
              isInteractive && !disabled && 'cursor-grab hover:scale-105 active:cursor-grabbing',
              classNames?.handle
            )}
            data-testid="progress-handle"
            style={{
              position: 'absolute',
              left: `${Math.max(0, Math.min(100, percentage))}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              transition: isDragging ? 'none' : 'all 0.2s ease-in-out',
            }}
          />
        )}
      </div>
    )
  }
)

Progress.displayName = 'Progress'

export default Progress
